
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UploadModal from './UploadModal';
import EditCertificateModal from './EditCertificateModal';
import { getCertificateTypes, getCertificateTypesSync, DEFAULT_CERT_TYPES } from '../../../../services/certificateTypes';
import { complianceService, landlordApi } from '../../../../services/api';
import { parseFlexibleDate } from '../../../../utils/complianceCalculator';
import CSVImportModal from '../../../Shared/CSVImportModal';

interface CertificateListProps {
    propertyId: string;
    certificates: any[];
    onRefresh: () => void;
    hasGasInstallation?: boolean;
    onViewProperty?: () => void;
}

const CertificateList: React.FC<CertificateListProps> = ({ propertyId, certificates, onRefresh, hasGasInstallation = true, onViewProperty }) => {
    const navigate = useNavigate();
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [selectedCert, setSelectedCert] = useState<any | null>(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isManageModalOpen, setIsManageModalOpen] = useState(false);
    // When set, the UploadModal runs in attach-file mode against this cert
    // (preserves cert id / audit history). Used for metadata-only certs
    // imported via CSV that don't yet have a PDF in S3.
    const [attachCertId, setAttachCertId] = useState<string | null>(null);
    // For the Replace flow: id of the OLD cert to delete once the new one uploads.
    const [replaceCertId, setReplaceCertId] = useState<string | null>(null);
    const [certTypes, setCertTypes] = useState(getCertificateTypesSync() || DEFAULT_CERT_TYPES);
    const [visibleCertTypes, setVisibleCertTypes] = useState<any[]>([]);
    const [showCertImportModal, setShowCertImportModal] = useState(false);

    // Tracks whether we've finished loading saved visibility for this
    // property. Without this guard the persist effect below would fire on
    // first render with the default empty [] state and overwrite the
    // user's saved preference before we'd even read it back.
    const [hydrated, setHydrated] = useState(false);

    // Load cert types and visibility
    useEffect(() => {
        setHydrated(false);
        getCertificateTypes()
            .then((types: any) => {
                setCertTypes(types);
                const saved = localStorage.getItem(`certVisibility_${propertyId}`);
                if (saved) {
                    const keys = JSON.parse(saved);
                    setVisibleCertTypes(types.filter((t: any) => keys.includes(t.key)));
                } else {
                    setVisibleCertTypes(types); // Default to all
                }
            })
            .catch((error: any) => {
                console.error('[CertificateList] Load error:', error);
                setCertTypes(DEFAULT_CERT_TYPES);
                setVisibleCertTypes(DEFAULT_CERT_TYPES);
            })
            .finally(() => setHydrated(true));
    }, [propertyId]);

    // Persist changes — including the empty-array case (user unticked
    // every cert type). The previous guard `length > 0` skipped that, so
    // the choice was silently dropped on the next render / reload.
    useEffect(() => {
        if (!hydrated) return;
        localStorage.setItem(`certVisibility_${propertyId}`, JSON.stringify(visibleCertTypes.map(v => v.key)));
    }, [visibleCertTypes, propertyId, hydrated]);

    // Debug certificates
    useEffect(() => {
        console.log('[CertificateList] Props:', { propertyId, certificates });
    }, [certificates, propertyId]);

    const getCert = (typeKey: string) => {
        return certificates.find(c => {
            const cType = (c.type || '').toLowerCase().replace(/[\s_-]/g, '');
            const cName = (c.name || '').toLowerCase().replace(/[\s_-]/g, '');
            const tk = typeKey.toLowerCase().replace(/[\s_-]/g, '');

            if (tk.includes('gas')) return cType.includes('gas') || cType.includes('cp12') || cName.includes('gas');
            if (tk.includes('epc')) return cType.includes('epc') || cType.includes('energy') || cName.includes('epc') || cName.includes('energy');
            if (tk.includes('eicr')) return cType.includes('eicr') || cType.includes('electrical') || cName.includes('eicr') || cName.includes('electrical');
            if (tk.includes('deposit')) return cType.includes('deposit') || cName.includes('deposit');
            if (tk.includes('tenancy')) return cType.includes('tenancy') || cType.includes('agreement') || cName.includes('tenancy');
            if (tk.includes('smoke')) return cType.includes('smoke') || cName.includes('smoke');
            if (tk.includes('carbon')) return cType.includes('carbon') || cName.includes('carbon');
            
            return c.type === typeKey || c.name === typeKey;
        });
    };

    const getStatus = (cert: any) => {
        if (!cert) return { label: 'Missing', color: 'red', icon: 'fa-times-circle' };

        const now = new Date();
        const expiry = parseFlexibleDate(cert.expires) || parseFlexibleDate(cert.expiryDate);
        if (!expiry) return { label: 'Expiry Missing', color: 'red', icon: 'fa-question-circle' };
        const diffTime = expiry.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return { label: 'Expired', color: 'red', icon: 'fa-exclamation-circle' };
        if (diffDays <= 30) return { label: 'Expiring Soon', color: 'yellow', icon: 'fa-clock' };
        return { label: 'Valid', color: 'green', icon: 'fa-check-circle' };
    };

    const handleUploadClick = (typeKey: string, existingCert?: any) => {
        setSelectedType(typeKey);
        setAttachCertId(null);
        // If there's already a cert of this type, treat this as a REPLACE:
        // remember its id so the modal can delete it after the new upload
        // lands. Prevents duplicate certs shadowing the new expiry in the
        // Litigation Readiness score.
        setReplaceCertId(existingCert?.id || null);
        setIsUploadModalOpen(true);
    };

    const handleAttachClick = (cert: any, typeKey: string) => {
        // Attach a file to an EXISTING metadata-only cert (no S3 key yet).
        // The modal will skip the expiry input and call attach-file.
        setSelectedType(typeKey);
        setAttachCertId(cert.id);
        setIsUploadModalOpen(true);
    };

    const handleEditClick = (cert: any) => {
        setSelectedCert(cert);
        setIsEditModalOpen(true);
    };

    const handleDelete = async (cert: any) => {
        if (!window.confirm(`Delete ${cert.type} certificate? This will trigger re-analysis on next upload.`)) {
            return;
        }
        try {
            await complianceService.deleteCertificate(cert.id, propertyId);
            alert('Certificate deleted successfully');
            onRefresh();
        } catch (error: any) {
            console.error('[CertificateList] Delete failed:', error);
            alert('Failed to delete certificate: ' + (error.response?.data?.message || error.message));
        }
    };

    // Manage Modal State Extensions
    const [editingCertKey, setEditingCertKey] = useState<string | null>(null);
    const [tempLabel, setTempLabel] = useState('');

    const PROTECTED_CERT_TYPES = [
        'Gas Safety',
        'Electrical (EICR)',
        'Energy (EPC)',
        'Landlord Insurance',
        'Tenancy Agreement'
    ];

    const handleRenameCertType = async (oldKey: string) => {
        if (!tempLabel.trim() || tempLabel === oldKey) {
            setEditingCertKey(null);
            return;
        }

        try {
            const { saveCertificateTypes } = await import('../../../../services/certificateTypes');
            const newTypes = certTypes.map((t: any) =>
                t.key === oldKey ? { ...t, key: tempLabel.trim() } : t
            );

            await saveCertificateTypes(newTypes);
            setCertTypes(newTypes);

            // Update visibility state if renamed
            setVisibleCertTypes(prev => prev.map(v => v.key === oldKey ? { ...v, key: tempLabel.trim() } : v));

            setEditingCertKey(null);
        } catch (error) {
            alert('Failed to save certificate name');
        }
    };

    return (
        <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
                <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Compliance & Certificates</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Manage all compliance documents for this property.</p>
                </div>
                <div className="flex gap-2">
                    {onViewProperty && (
                        <button
                            type="button"
                            onClick={onViewProperty}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-black rounded-xl text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-all active:scale-95"
                            title="View full property details"
                        >
                            <i className="fas fa-expand-alt mr-2"></i>
                            View Property
                        </button>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setShowCertImportModal(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-black rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 transition-all active:scale-95"
                    >
                        <i className="fas fa-file-import mr-2"></i>
                        Bulk Upload
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsManageModalOpen(true)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-xl text-sm font-black text-gray-700 hover:bg-gray-100 focus:outline-none transition-all"
                    >
                        <i className="fas fa-cog mr-2"></i>
                        Manage
                    </button>
                </div>
            </div>

            <ul className="divide-y divide-gray-200">
                {visibleCertTypes.map((type: any) => {
                    const cert = getCert(type.key);
                    const status = getStatus(cert);
                    const isGasSafety = type.key === 'Gas Safety';
                    const isGasDisabled = isGasSafety && !hasGasInstallation;

                    return (
                        <li key={type.key} className={`px-4 py-4 sm:px-6 hover:bg-gray-50 transition-colors ${isGasDisabled ? 'opacity-50 bg-gray-50 cursor-not-allowed' : ''}`} title={isGasDisabled ? 'Not applicable - Property is 100% electric' : ''}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center min-w-0">
                                    <div className={`flex-shrink-0 h-10 w-10 rounded-full bg-${type.color}-100 flex items-center justify-center`}>
                                        <i className={`fas ${type.icon} text-${type.color}-600`}></i>
                                    </div>
                                    <div className="ml-4 min-w-0">
                                        <div className="flex items-center">
                                            <p className="text-sm font-medium text-purple-600 truncate">{type.key}</p>
                                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${status.color}-100 text-${status.color}-800`}>
                                                <i className={`fas ${status.icon} mr-1`}></i>
                                                {status.label}
                                            </span>
                                            {cert?.contentHash && (
                                                <span
                                                    className="ml-2 inline-flex items-center text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded cursor-help"
                                                    title={`Immutable Evidence Vault\nSHA-256: ${cert.contentHash}\nTimestamp: ${cert.provenanceTimestamp}`}
                                                    aria-label="Verified Immutable Evidence"
                                                >
                                                    <i className="fas fa-shield-alt mr-1"></i> Verified
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 flex text-sm text-gray-500">
                                            {cert ? (() => {
                                                const d = parseFlexibleDate(cert.expires) || parseFlexibleDate(cert.expiryDate);
                                                return <span>Expires: {d ? d.toLocaleDateString('en-GB') : 'Not set'}</span>;
                                            })() : (
                                                <span>No certificate on file</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                                    {cert && (cert.storageKey || cert.s3Key) && (
                                        <a
                                            href={cert.url} // Presigned URL from API
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none"
                                        >
                                            View
                                        </a>
                                    )}
                                    {cert && !(cert.storageKey || cert.s3Key) && (
                                        <button
                                            type="button"
                                            onClick={() => handleAttachClick(cert, type.key)}
                                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none"
                                            title="This cert has no PDF on file (metadata-only import). Attach a PDF without losing the existing record."
                                        >
                                            <i className="fas fa-paperclip mr-1"></i>
                                            Attach PDF
                                        </button>
                                    )}
                                    {!cert && (
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/certificate-orders/new?propertyId=${propertyId}&certType=${encodeURIComponent(type.key)}`)}
                                            disabled={isGasDisabled}
                                            className={`inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white ${isGasDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none`}
                                            title={isGasDisabled ? 'Not required - Property is 100% electric' : 'Order professional inspection'}
                                        >
                                            <i className="fas fa-shopping-cart mr-1"></i>
                                            Order
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleUploadClick(type.key, cert)}
                                        disabled={isGasDisabled}
                                        className={`inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white ${isGasDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} focus:outline-none`}
                                        title={isGasDisabled ? 'Not required - Property is 100% electric' : ''}
                                    >
                                        {cert ? 'Replace' : 'Upload'}
                                    </button>
                                    {cert && (
                                        <button
                                            type="button"
                                            onClick={() => handleEditClick(cert)}
                                            className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                                            title="Edit certificate details"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                    )}
                                    {cert && !PROTECTED_CERT_TYPES.includes(type.key) && (
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(cert)}
                                            className="inline-flex items-center px-3 py-1 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none"
                                            title="Delete certificate"
                                        >
                                            <i className="fas fa-trash-alt mr-1"></i>
                                            Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {/* Upload Modal */}
            <UploadModal
                isOpen={isUploadModalOpen}
                onClose={() => { setIsUploadModalOpen(false); setAttachCertId(null); setReplaceCertId(null); }}
                onSuccess={onRefresh}
                propertyId={propertyId}
                certificateType={selectedType || ''}
                existingCertId={attachCertId || undefined}
                replaceCertId={replaceCertId || undefined}
            />

            {/* Edit Modal */}
            <EditCertificateModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                certificate={selectedCert}
                propertyId={propertyId}
                onSuccess={onRefresh}
            />

            {/* Manage Modal */}
            {isManageModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setIsManageModalOpen(false)}></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div>
                                <h3 className="text-lg leading-6 font-medium text-gray-900">Certificate Customization</h3>
                                <p className="mt-2 text-sm text-gray-500">Toggle visibility or rename your custom certificate labels.</p>
                            </div>
                            <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
                                {certTypes.map((type: any) => (
                                    <div key={type.key} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                                        <label className="flex items-center flex-1 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={visibleCertTypes.some((vt: any) => vt.key === type.key)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setVisibleCertTypes([...visibleCertTypes, type]);
                                                    } else {
                                                        setVisibleCertTypes(visibleCertTypes.filter((vt: any) => vt.key !== type.key));
                                                    }
                                                }}
                                                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                            />
                                            {editingCertKey === type.key ? (
                                                <input
                                                    type="text"
                                                    autoFocus
                                                    value={tempLabel}
                                                    onChange={(e) => setTempLabel(e.target.value)}
                                                    onBlur={() => handleRenameCertType(type.key)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleRenameCertType(type.key)}
                                                    className="ml-2 text-sm border-gray-300 rounded px-1 flex-1 focus:ring-1 focus:ring-indigo-500"
                                                />
                                            ) : (
                                                <span className="ml-2 text-sm text-gray-700">{type.key}</span>
                                            )}
                                        </label>

                                        {/* All cert labels are renamable. PROTECTED_CERT_TYPES
                                            still guards deletion (delete would orphan certs of
                                            that type), but renaming is purely a display change
                                            — the underlying cert.type field on uploaded certs
                                            doesn't shift, and the keyword-based getCert() lookup
                                            stays robust as long as the new label keeps a
                                            recognisable root word (e.g. "Annual Gas Check"
                                            still matches via "gas"). */}
                                        {editingCertKey !== type.key && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEditingCertKey(type.key);
                                                    setTempLabel(type.key);
                                                }}
                                                className="ml-2 text-xs text-indigo-600 hover:text-indigo-800"
                                            >
                                                Rename
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    onClick={() => setIsManageModalOpen(false)}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <CSVImportModal
                isOpen={showCertImportModal}
                onClose={() => setShowCertImportModal(false)}
                allowFileAttachments={true}
                onImport={async (data, zipFile) => {
                    // Enrich each CSV row with an S3 file pointer if a matching
                    // PDF is found in the companion zip. Rows without a match
                    // still go through as metadata-only so the user can attach
                    // a file later via the "Attach PDF" button.
                    let enriched = data;
                    if (zipFile) {
                        const JSZip = (await import('jszip')).default;
                        const zip = await JSZip.loadAsync(zipFile);
                        // Build a case-insensitive index of file name -> JSZip entry
                        const index: Record<string, any> = {};
                        zip.forEach((path, entry) => {
                            if (!entry.dir) {
                                const base = path.split('/').pop() || path;
                                index[base.toLowerCase()] = entry;
                            }
                        });

                        const sha256 = async (buf: ArrayBuffer): Promise<string> => {
                            const h = await crypto.subtle.digest('SHA-256', buf);
                            return Array.from(new Uint8Array(h)).map(b => b.toString(16).padStart(2, '0')).join('');
                        };

                        enriched = [];
                        for (const row of data) {
                            const wanted = (row.pdf_filename || row.pdfFilename || '').toString().trim();
                            if (!wanted) { enriched.push(row); continue; }
                            const entry = index[wanted.toLowerCase()];
                            if (!entry) {
                                enriched.push({ ...row, _attachStatus: 'no-pdf-in-zip' });
                                continue;
                            }
                            try {
                                const blob = await entry.async('blob');
                                const inferType = wanted.toLowerCase().endsWith('.pdf') ? 'application/pdf'
                                    : wanted.toLowerCase().match(/\.(jpe?g)$/) ? 'image/jpeg'
                                    : wanted.toLowerCase().endsWith('.png') ? 'image/png'
                                    : blob.type || 'application/octet-stream';
                                const file = new File([blob], wanted, { type: inferType });

                                const pres = await complianceService.getPresignedUploadUrl({
                                    fileName: file.name,
                                    fileType: file.type,
                                    propertyId,
                                });
                                const { uploadUrl, s3Key } = pres.data?.data || {};
                                if (!uploadUrl || !s3Key) throw new Error('no presigned URL');

                                const ab = await file.arrayBuffer();
                                const putRes = await fetch(uploadUrl, { method: 'PUT', body: ab, headers: { 'Content-Type': file.type } });
                                if (!putRes.ok) throw new Error('S3 PUT ' + putRes.status);

                                const hash = await sha256(ab);
                                enriched.push({
                                    ...row,
                                    s3Key,
                                    fileName: file.name,
                                    contentHash: hash,
                                    _attachStatus: 'attached',
                                });
                            } catch (uploadErr: any) {
                                console.warn('[CertBulk] PDF upload failed for', wanted, uploadErr?.message);
                                enriched.push({ ...row, _attachStatus: 'upload-failed' });
                            }
                        }
                        const attached = enriched.filter(r => r._attachStatus === 'attached').length;
                        const missed  = enriched.filter(r => r._attachStatus === 'no-pdf-in-zip').length;
                        const failed  = enriched.filter(r => r._attachStatus === 'upload-failed').length;
                        console.log('[CertBulk] zip processing:', { rows: enriched.length, attached, missedInZip: missed, uploadFailed: failed });
                    }
                    await complianceService.bulkImportCertificates(propertyId, enriched);
                    onRefresh();
                }}
                title="Import Certificates"
                headerRequirements={[
                    { id: 'type', label: 'Document Type', required: true, description: 'gas_safety, epc, eicr, or license', example: 'gas_safety' },
                    { id: 'expiryDate', label: 'Expiry Date', required: true, description: 'Date format YYYY-MM-DD', example: '2025-12-31' },
                    { id: 'status', label: 'Status', required: true, description: 'VALID, EXPIRED, or PENDING', example: 'VALID' },
                    { id: 'reference', label: 'Reference #', required: false, description: 'Unique certificate number', example: 'GS-12345' },
                    { id: 'pdf_filename', label: 'PDF Filename', required: false, description: 'Optional. Filename inside the companion .zip to attach as the cert PDF.', example: 'gas_safety_aug2025.pdf' }
                ]}
                templateHeaders={['type', 'expiryDate', 'status', 'reference', 'pdf_filename']}
                sampleData="gas_safety,2025-12-31,VALID,GS-12345,gas_safety_aug2025.pdf"
            />
        </div>
    );
};

export default CertificateList;
